import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Flag, AlertTriangle, CheckCircle, XCircle, Clock, Search, 
  User, Shield, Ban, AlertOctagon, Eye, Loader2, RefreshCw
} from 'lucide-react';

const API = window.location.origin + '/api';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  reviewed: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
  dismissed: 'bg-slate-100 text-slate-600',
};

const REASON_LABELS = {
  scam: 'Scam / Fraud',
  no_show: 'No-Show',
  harassment: 'Harassment',
  fake_booking: 'Fake Booking',
  other: 'Other',
};

export default function UserReports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [adminNotes, setAdminNotes] = useState('');
  const [selectedAction, setSelectedAction] = useState('');

  const fetchReports = useCallback(async () => {
    setIsLoading(true);
    const token = localStorage.getItem('token');
    try {
      const status = activeTab === 'all' ? undefined : activeTab;
      const response = await axios.get(`${API}/admin/reports/users`, {
        params: { status, limit: 100 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data.reports || []);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setIsLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleUpdateReport = async (reportId, status, action = null) => {
    setIsProcessing(true);
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/admin/reports/users/${reportId}?status=${status}&action=${action || ''}&admin_notes=${encodeURIComponent(adminNotes)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Report ${status}${action ? ` - ${action} action taken` : ''}`);
      setSelectedReport(null);
      setAdminNotes('');
      setSelectedAction('');
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update report');
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredReports = reports.filter(r => 
    r.reporter_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.reported_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.reported_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.reason?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />
      
      <main className="flex-1 p-4 md:p-8 md:ml-64 pt-16 md:pt-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">User Reports</h1>
            <p className="text-slate-500">Review and manage user reports for fraud protection</p>
          </div>
          <Button onClick={fetchReports} variant="outline" disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {reports.filter(r => r.status === 'pending').length}
                </p>
                <p className="text-sm text-slate-500">Pending Review</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {reports.filter(r => r.reason === 'scam').length}
                </p>
                <p className="text-sm text-slate-500">Scam Reports</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {reports.filter(r => r.status === 'resolved').length}
                </p>
                <p className="text-sm text-slate-500">Resolved</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Flag className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{reports.length}</p>
                <p className="text-sm text-slate-500">Total Reports</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs and Search */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="reviewed">Reviewed</TabsTrigger>
              <TabsTrigger value="resolved">Resolved</TabsTrigger>
              <TabsTrigger value="dismissed">Dismissed</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </Tabs>

        {/* Reports Table */}
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Reporter</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Reported User</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Reason</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-500 mx-auto" />
                  </td>
                </tr>
              ) : filteredReports.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                    No reports found
                  </td>
                </tr>
              ) : (
                filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900">{report.reporter_name}</p>
                        <p className="text-xs text-slate-500 capitalize">{report.reporter_role}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900">{report.reported_name}</p>
                        <p className="text-xs text-slate-500">{report.reported_email}</p>
                        {report.reported_total_reports > 1 && (
                          <span className="text-xs text-red-600 font-medium">
                            ({report.reported_total_reports} total reports)
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        report.reason === 'scam' ? 'bg-red-100 text-red-700' :
                        report.reason === 'harassment' ? 'bg-orange-100 text-orange-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {report.reason === 'scam' && <AlertOctagon className="w-3 h-3" />}
                        {REASON_LABELS[report.reason] || report.reason}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[report.status]}`}>
                        {report.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {formatDate(report.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedReport(report)}
                        data-testid={`review-report-${report.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Review
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Report Review Dialog */}
      <Dialog open={!!selectedReport} onOpenChange={(open) => !open && setSelectedReport(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-red-500" />
              Review Report
            </DialogTitle>
            <DialogDescription>
              Review the report details and take appropriate action
            </DialogDescription>
          </DialogHeader>
          
          {selectedReport && (
            <div className="space-y-4 mt-4">
              {/* Reporter Info */}
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-xs text-slate-500 mb-1">Reporter</p>
                <p className="font-medium text-slate-900">{selectedReport.reporter_name}</p>
                <p className="text-sm text-slate-600 capitalize">{selectedReport.reporter_role}</p>
              </div>
              
              {/* Reported User Info */}
              <div className="p-3 bg-red-50 rounded-xl">
                <p className="text-xs text-red-600 mb-1">Reported User</p>
                <p className="font-medium text-slate-900">{selectedReport.reported_name}</p>
                <p className="text-sm text-slate-600">{selectedReport.reported_email}</p>
                <p className="text-sm text-slate-600 capitalize">{selectedReport.reported_user_role}</p>
                {selectedReport.reported_total_reports > 1 && (
                  <p className="text-sm text-red-600 font-medium mt-1">
                    ⚠️ This user has {selectedReport.reported_total_reports} total reports
                  </p>
                )}
              </div>
              
              {/* Reason & Description */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-1">Reason</p>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  selectedReport.reason === 'scam' ? 'bg-red-100 text-red-700' :
                  selectedReport.reason === 'harassment' ? 'bg-orange-100 text-orange-700' :
                  'bg-slate-100 text-slate-700'
                }`}>
                  {REASON_LABELS[selectedReport.reason] || selectedReport.reason}
                </span>
              </div>
              
              <div>
                <p className="text-sm font-medium text-slate-700 mb-1">Description</p>
                <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                  {selectedReport.description}
                </p>
              </div>
              
              {/* Admin Notes */}
              <div>
                <Label>Admin Notes</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about your decision..."
                  className="mt-1.5"
                  rows={2}
                />
              </div>
              
              {/* Action Selection */}
              <div>
                <Label>Take Action on Reported User</Label>
                <Select value={selectedAction} onValueChange={setSelectedAction}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Select action (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No action</SelectItem>
                    <SelectItem value="warn">Send Warning</SelectItem>
                    <SelectItem value="suspend">Suspend Account</SelectItem>
                    <SelectItem value="ban">Ban Account</SelectItem>
                    <SelectItem value="clear">Clear Report (false report)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => handleUpdateReport(selectedReport.id, 'dismissed')}
                  disabled={isProcessing}
                  className="flex-1"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Dismiss
                </Button>
                <Button
                  onClick={() => handleUpdateReport(selectedReport.id, 'resolved', selectedAction || null)}
                  disabled={isProcessing}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {isProcessing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-1" />
                  )}
                  Resolve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
